import React, { useState, useRef } from 'react';
import { useModalDismiss } from '../../../hooks/useModalDismiss';
import axios from '../../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUpload, faSpinner, faCircleCheck, faTriangleExclamation, faFileExcel } from '@fortawesome/free-solid-svg-icons';

// Human-readable labels for each error group returned by the preview endpoint.
const ERROR_GROUP_LABELS = {
  structure: 'Structure du fichier',
  columns: 'Colonnes (combinaisons)',
  missingColumns: 'Combinaisons manquantes',
  refs: 'Prestataires (Ref)',
  duplicates: 'Doublons',
  values: 'Valeurs',
  overAllocation: 'Dépassement d\'objectif',
  completes: 'Quotas déjà complétés',
};

/**
 * Excel bulk-assignment: upload a file -> server validates the whole file atomically
 * (preview) -> on confirm, replay the validated plan through the existing per-cell
 * assign-supervisor endpoint (same server-side rules). No partial import: the confirm
 * button is blocked until the file is fully valid.
 */
const ExcelAssignmentModal = ({ quotaProjectId, onClose, onImportComplete }) => {
  // File-import form: only Escape and the explicit buttons dismiss it.
  const dismiss = useModalDismiss(onClose, { backdropClose: false });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null); // { valid, errors, summary, plan, warnings }
  const [committing, setCommitting] = useState(false);
  const [commitProgress, setCommitProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setError('');
  };

  // Read a File as base64 (strip the data-URL prefix).
  const fileToBase64 = (f) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('read error'));
    reader.readAsDataURL(f);
  });

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setPreview(null);

    // Read the file first. If it was edited/moved on disk after being selected, the
    // browser's File reference is stale and reading throws — prompt a re-selection.
    let fileBase64;
    try {
      fileBase64 = await fileToBase64(file);
    } catch (readErr) {
      setError('Impossible de lire le fichier. Il a peut-être été modifié depuis sa sélection — veuillez le re-sélectionner.');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploading(false);
      return;
    }

    try {
      // Send as base64 JSON (not multipart) — robust across dev/prod servers.
      const response = await axios.post(
        `/quotas/projects/${quotaProjectId}/assignments/preview`,
        { file_base64: fileBase64, filename: file.name }
      );
      setPreview(response.data);
    } catch (err) {
      if (err.response?.data?.errors) {
        // Laravel validation error (e.g. wrong file type)
        const first = Object.values(err.response.data.errors)[0];
        setError(Array.isArray(first) ? first[0] : String(first));
      } else {
        setError(err.response?.data?.message || 'Erreur lors de l\'analyse du fichier.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview?.valid || !preview.plan?.length) return;
    setCommitting(true);
    setError('');
    setCommitProgress({ done: 0, total: preview.plan.length });
    try {
      // Replay the validated plan cell-by-cell through the existing assignment endpoint.
      for (let i = 0; i < preview.plan.length; i++) {
        const entry = preview.plan[i];
        await axios.post(
          `/quotas/projects/${quotaProjectId}/cells/${entry.quota_cell_id}/assign-supervisor`,
          {
            supervisor_ids: entry.supervisor_ids,
            assignments: entry.assignments,
          }
        );
        setCommitProgress({ done: i + 1, total: preview.plan.length });
      }
      if (onImportComplete) await onImportComplete();
      onClose();
    } catch (err) {
      setError(
        (err.response?.data?.message || 'Erreur lors de l\'enregistrement des assignations.') +
        ` (${commitProgress.done}/${preview.plan.length} combinaison(s) traitée(s) avant l'erreur.)`
      );
    } finally {
      setCommitting(false);
    }
  };

  const errorGroups = preview && preview.errors ? Object.entries(preview.errors) : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...dismiss}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={faFileExcel} className="text-green-600" />
            Importer les assignations (Excel)
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Téléchargez d'abord le modèle, remplissez les quotas par prestataire et par combinaison,
            puis importez le fichier. Le fichier est validé entièrement avant tout enregistrement.
            Il doit contenir <strong>toutes les combinaisons</strong> du projet et <strong>remplace</strong> leurs
            assignations : une case vide (ou 0) retire le prestataire de la combinaison — sauf s'il y a déjà
            effectué du travail, auquel cas son quota est ramené au nombre déjà complété.
          </p>

          {/* File picker */}
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              onClick={(e) => { e.target.value = null; }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
            />
            <button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {uploading ? (<><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Analyse...</>)
                : (<><FontAwesomeIcon icon={faUpload} className="mr-2" />Analyser</>)}
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Validation errors, grouped */}
          {preview && !preview.valid && errorGroups.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-red-700 font-semibold text-sm">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                Le fichier contient des erreurs — corrigez-les puis réimportez.
              </div>
              {errorGroups.map(([group, msgs]) => (
                <div key={group} className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-1.5 bg-red-50 text-xs font-semibold text-red-800 uppercase tracking-wide">
                    {ERROR_GROUP_LABELS[group] || group} ({msgs.length})
                  </div>
                  <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                    {msgs.map((m, idx) => (<li key={idx}>{m}</li>))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {/* Prestataires the file drops who have already completed work here: the
              import keeps them at exactly their completes instead of removing them, so
              say who and how much before the CT confirms. */}
          {preview && preview.retained && preview.retained.length > 0 && (
            <div className="border border-blue-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-blue-50 text-xs font-semibold text-blue-800 uppercase tracking-wide">
                Prestataires conservés ({preview.retained.length})
              </div>
              <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside max-h-40 overflow-y-auto">
                {preview.retained.map((r, idx) => (
                  <li key={idx}>
                    <strong>{r.ref}</strong>{r.suspended ? ' (suspendu)' : ''} sur « {r.cell_label} » :
                    {' '}absent du fichier, mais {r.completes} déjà complété(s) — son quota passe de
                    {' '}{r.previous} à {r.completes} au lieu d'être retiré.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnings (non-blocking) */}
          {preview && preview.warnings && preview.warnings.length > 0 && (
            <div className="border border-amber-200 rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-amber-50 text-xs font-semibold text-amber-800 uppercase tracking-wide">Avertissements</div>
              <ul className="px-4 py-2 space-y-1 text-sm text-gray-700 list-disc list-inside">
                {preview.warnings.map((m, idx) => (<li key={idx}>{m}</li>))}
              </ul>
            </div>
          )}

          {/* Valid summary */}
          {preview && preview.valid && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-800 font-semibold text-sm mb-1">
                <FontAwesomeIcon icon={faCircleCheck} />
                Fichier valide
              </div>
              <p className="text-sm text-green-800">
                {preview.summary.prestataires} prestataire(s), {preview.summary.combinaisons} combinaison(s),
                {' '}{preview.summary.assignments} assignation(s) seront créées / mises à jour.
              </p>
            </div>
          )}

          {committing && (
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              Enregistrement... {commitProgress.done}/{commitProgress.total}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={committing}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!preview?.valid || committing || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {committing ? 'Enregistrement...' : 'Confirmer l\'import'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExcelAssignmentModal;
